import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { scrollToId } from "./Reveal";

const PLATFORM_MENU = [
  ["DMC Operations", "/platform#dmc-operations"],
  ["Rate Management", "/platform#rate-management"],
  ["Package Builder", "/platform#package-builder"],
  ["Inquiry Management", "/platform#inquiry-management"],
  ["Quotation Engine", "/platform#quotation-engine"],
  ["Partner Distribution", "/platform#partner-distribution"],
  ["Agent Channel", "/platform#agent-channel"],
  ["Analytics", "/platform#analytics"],
];

const SOLUTIONS_MENU = [
  ["For DMCs", "/#who-dmcs"],
  ["For B2B Distributors", "/#who-distributors"],
  ["For Travel Agents", "/#who-agents"],
  ["For Tour Operators", "/#who-operators"],
];

export const Wordmark = ({ light = false }) => (
  <a href="/" data-testid="header-brand-logo" className={`font-serif text-2xl tracking-tight ${light ? "text-paper" : "text-ink"}`}>
    Travel-Tek<span className="text-terra">.</span>
  </a>
);

const Dropdown = ({ label, items, tid }) => (
  <div className="group relative">
    <button data-testid={`nav-dropdown-${tid}`} className="flex items-center gap-1.5 py-2 text-sm font-medium text-stone2 transition-colors duration-300 group-hover:text-ink">
      {label} <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
    </button>
    <div className="invisible absolute left-0 top-full w-64 translate-y-1 pt-1 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
      <div className="rounded-2xl border border-sand bg-white p-2 shadow-[0_25px_50px_-20px_rgba(11,31,51,0.3)]">
        {items.map(([label, href]) => (
          <a
            key={label}
            href={href}
            data-testid={`nav-menu-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className="block rounded-lg px-3.5 py-2.5 text-sm font-medium text-stone2 transition-colors duration-200 hover:bg-parch hover:text-ink"
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  </div>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const home = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const anchor = (hash) => (e) => {
    if (home) scrollToId(hash)(e);
    setOpen(false);
  };
  const href = (hash) => (home ? hash : `/${hash}`);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || !home ? "border-b border-sand bg-paper/90 backdrop-blur-md" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="container-x flex h-[70px] items-center justify-between">
        <Wordmark />
        <nav className="hidden items-center gap-7 xl:flex">
          <Dropdown label="Platform" items={PLATFORM_MENU} tid="platform" />
          <a href="/how-it-works" data-testid="nav-link-how-it-works" className="text-sm font-medium text-stone2 transition-colors duration-300 hover:text-ink">
            How It Works
          </a>
          <Dropdown label="Solutions" items={SOLUTIONS_MENU} tid="solutions" />
          <a href={href("#pricing")} onClick={anchor("#pricing")} data-testid="nav-link-pricing" className="text-sm font-medium text-stone2 transition-colors duration-300 hover:text-ink">
            Pricing
          </a>
        </nav>
        <div className="flex items-center gap-2.5">
          <a
            href={href("#demo")}
            onClick={anchor("#demo")}
            data-testid="nav-request-demo-btn"
            className="group hidden items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-terra hover:text-ink sm:inline-flex"
          >
            Book a Demo
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-btn"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-sand text-ink xl:hidden"
            aria-label="Toggle menu"
          >
            {open ? "✕" : "☰"}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="border-b border-sand bg-paper/95 backdrop-blur-md xl:hidden"
          >
            <nav className="container-x flex flex-col gap-1 py-4">
              <a href="/platform" onClick={() => setOpen(false)} data-testid="nav-mobile-link-platform" className="rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-parch">Platform</a>
              <a href="/how-it-works" onClick={() => setOpen(false)} data-testid="nav-mobile-link-how-it-works" className="rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-parch">How It Works</a>
              <a href={href("#who")} onClick={anchor("#who")} data-testid="nav-mobile-link-solutions" className="rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-parch">Solutions</a>
              <a href={href("#pricing")} onClick={anchor("#pricing")} data-testid="nav-mobile-link-pricing" className="rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-parch">Pricing</a>
              <a
                href={href("#demo")}
                onClick={anchor("#demo")}
                data-testid="nav-mobile-request-demo-btn"
                className="mt-2 rounded-full bg-ink px-5 py-3 text-center text-sm font-semibold text-paper"
              >
                Book a Demo
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
